/*
	Login page with email and password fields
	Handles authentication and stores user info in localStorage
	Shows the instance logo and its name beside the title
*/

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { login, getUserByEmail } from '../features/users';
import {
	InstanceMark,
	instanceEnvironmentNotice,
	useInstance,
} from '../features/instance';

interface LocationState {
	message?: string;
}

// Login component
const Login: React.FC = () => {
	// State management for form fields and error messages
	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [error, setError] = useState('');
	const navigate = useNavigate();
	const location = useLocation();
	const state = location.state as LocationState;
	const expiredMessage = state?.message || '';
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { branding } = useInstance();
	const displayName = branding?.displayName?.trim() ?? '';
	const environmentNotice = branding
		? instanceEnvironmentNotice(branding.profile)
		: null;
	const nonProd = environmentNotice !== null;

	// Handle form submission
	const handleLogin = async (e: React.FormEvent) => {
		e.preventDefault();
		// desactivation du bouton pendant le traitement
		setIsSubmitting(true);
		try {
			const data = await login(email, password);
			localStorage.setItem('token', data.token);
			localStorage.setItem('email', email);
			// on demande à lapi le name et firstName grâce à l'adresse email
			const userInfo = await getUserByEmail(email);
			localStorage.setItem('userId', userInfo.id);
			localStorage.setItem('name', userInfo.name);
			localStorage.setItem('firstName', userInfo.firstName);

			navigate('/app');
		} catch (err: unknown) {
			// Improved error handling
			if (typeof err === 'object' && err !== null && 'response' in err) {
				// @ts-expect-error: err.response may exist on axios errors
				console.error('Login error:', err.response?.data || err);
			} else {
				// Generic error logging
				console.error('Login error Generic:', err);
			}
			// Set user-friendly error message
			let strErr = '';
			if (
				typeof err === 'object' &&
				err !== null &&
				'response' in err &&
				typeof err.response === 'object' &&
				err.response &&
				'data' in err.response &&
				typeof err.response.data === 'object' &&
				err.response.data !== null
			) {
				/*if ('error' in err.response.data) {
					strErr += ` ${err.response.data.error} : `;
				}*/
				if ('message' in err.response.data) {
					if (err.response.data.message === 'Invalid credentials.') {
						strErr += 'Identifiants invalides';
					} else strErr += err.response.data.message;
				}
			}
			if (strErr === '') {
				strErr = 'Erreur inconnue';
			} else {
				strErr = 'Erreur de connexion\r (' + strErr + ')';
			}
			setError(strErr);
			setIsSubmitting(false);
		}
	};

	// Render the login form
	return (
		<div
			className={`flex justify-center items-center min-h-screen px-4 bg-gradient-to-br ${
				nonProd ? 'from-amber-50 to-orange-100' : 'from-indigo-50 to-indigo-100'
			}`}
		>
			<div className="flex w-full max-w-4xl bg-white rounded-3xl shadow-2xl overflow-hidden">
				{/* Colonne gauche : logo de l'instance */}
				<div
					className={`hidden md:flex flex-col justify-center items-center w-1/2 p-10 ${
						nonProd ? 'bg-amber-50' : 'bg-indigo-50'
					}`}
				>
					<InstanceMark className="w-48 h-48 object-contain" />
				</div>

				{/* Colonne droite : Formulaire */}
				<div className="flex-1 p-10">
					<form onSubmit={handleLogin} className="flex flex-col">
						<InstanceMark className="w-24 h-24 object-contain mx-auto mb-6 md:hidden" />
						<h2
							className={`text-3xl font-extrabold text-center md:text-left ${
								environmentNotice ? 'mb-2' : 'mb-8'
							} ${nonProd ? 'text-amber-800' : 'text-indigo-700'}`}
						>
							Connexion
							{displayName
								? ` - ${displayName.toLocaleUpperCase('fr-FR')}`
								: ''}
						</h2>
						{environmentNotice && (
							<p className="text-sm font-semibold uppercase tracking-wide text-amber-700 text-center md:text-left mb-8">
								{environmentNotice}
							</p>
						)}

						{(expiredMessage || error) && (
							<p className="text-red-500 mb-4 text-center md:text-left font-medium">
								{expiredMessage || error}
							</p>
						)}

						<div className="mb-6">
							<label className="block text-sm font-medium mb-2 text-gray-700">
								Email
							</label>
							<input
								type="email"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
								placeholder="nom@exemple.com"
								required
							/>
						</div>

						<div className="mb-8">
							<label className="block text-sm font-medium mb-2 text-gray-700">
								Mot de passe
							</label>
							<input
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
								placeholder="••••••••"
								required
							/>
						</div>

						<button
							type="submit"
							className="
								w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold
								rounded-lg shadow-md
								focus:outline-none focus:ring-2 focus:ring-indigo-400 transition
								disabled:bg-indigo-300
								"
							disabled={isSubmitting}
						>
							{isSubmitting ? 'Connexion...' : 'Se connecter'}
						</button>
					</form>
				</div>
			</div>
		</div>
	);
};

// Export the component
export default Login;
